import { useState } from "react";
import type { ReactNode } from "react";
import {
  ArrowRight,
  KeyRound,
  Laptop,
  MonitorSmartphone,
  ShieldAlert,
  ShieldCheck,
  X,
} from "lucide-react";
import { Button } from "../ui/button";
import { Card, CardBody, CardFooter, CardHeader } from "../ui/card";
import { Notice } from "../ui/notice";
import { PageHeader } from "../ui/page-header";
import { UiLink } from "../ui/router";
import { StatusPill } from "../ui/status-pill";
import { RequireSession, canSpend } from "./guards";
import { currentReturnPath, describeCallback, handoffURL, isAuthorizeRequest } from "../oauth";
import type { AuthorizeSearch } from "../oauth";

/**
 * The SSO authorize screen — the browser leg of a desktop application's
 * sign-in.
 *
 * What this screen does NOT do is the point: it never navigates to
 * `redirect_uri`. Approving hands the whole request to
 * /v1/auth/oauth/authorize, which checks the callback against the client's
 * registered list before it issues a code and redirects. The portal shows
 * the callback so a person can recognise it; the platform is what decides
 * whether it is allowed.
 *
 * The design has no consent screen, so this is assembled from the ported
 * primitives only — page header, card, notice, status pill, button. Nothing
 * new is invented for it, because a control that exists on exactly one screen
 * is a control nobody recognises, and this is the one screen where a person
 * has to recognise what they are looking at.
 */
export function Authorize({ search }: { search: AuthorizeSearch }) {
  const [cancelled, setCancelled] = useState(false);

  if (!isAuthorizeRequest(search)) {
    return (
      <Outcome
        title="Nothing to authorize"
        description="This page is opened by an application when it needs your permission."
        tone="warning"
        noticeTitle="There is no request in this link"
        icon={<ShieldAlert className="size-4" aria-hidden />}
      >
        Open it from the application that needs access, or go back to your account.
      </Outcome>
    );
  }

  const callback = describeCallback(search.redirect_uri);
  const scopes = (search.scope ?? "").split(/\s+/).filter(Boolean);

  if (cancelled) {
    return (
      /* No redirect on cancel: the only thing that may send this browser to
         the application's callback is the platform, after it has validated
         the callback. */
      <Outcome
        title="Sign-in cancelled"
        description="You can close this window."
        tone="info"
        noticeTitle="Nothing was shared with the application"
        icon={<ShieldCheck className="size-4" aria-hidden />}
      >
        No code was issued and the application was not told anything about your account.
      </Outcome>
    );
  }

  return (
    <RequireSession>
      {(session) => (
        <div className="mx-auto max-w-3xl">
          <PageHeader
            title="Sign in to an application"
            description="An application on this machine is asking to use your suite account."
            eyebrow={
              <StatusPill tone="brand" dot pulse>
                Waiting for you
              </StatusPill>
            }
          />

          {canSpend(session) ? null : (
            <Notice
              tone="warning"
              title="Verify your phone first"
              icon={<ShieldAlert className="size-4" aria-hidden />}
              className="mb-5"
            >
              An application cannot be activated until your phone number is verified.{" "}
              <UiLink
                href="/verify-phone"
                search={{ user_id: session.user_id, next: currentReturnPath() }}
                className="font-semibold text-brand hover:text-brand-strong"
              >
                Verify it now
              </UiLink>{" "}
              and you will come straight back here.
            </Notice>
          )}

          <Card>
            <CardHeader
              title="Requesting application"
              description="Check these before you continue — they are what the application will be given."
            />
            <CardBody className="py-1">
              <dl className="divide-y divide-line-soft">
                <Row label="Application" icon={<KeyRound className="size-3.5" aria-hidden />}>
                  <code className="font-mono text-xs break-all text-ink">{search.client_id}</code>
                </Row>

                <Row label="Returns to" icon={<Laptop className="size-3.5" aria-hidden />}>
                  {callback ? (
                    <span className="flex flex-wrap items-center gap-2">
                      <code className="font-mono text-xs break-all text-ink">
                        {callback.origin}
                      </code>
                      {callback.loopback ? (
                        <StatusPill tone="success">on this machine</StatusPill>
                      ) : (
                        <StatusPill tone="warning">not this machine</StatusPill>
                      )}
                    </span>
                  ) : (
                    <StatusPill tone="danger">unreadable</StatusPill>
                  )}
                </Row>

                {search.installation_id ? (
                  <Row
                    label="Installation"
                    icon={<MonitorSmartphone className="size-3.5" aria-hidden />}
                  >
                    <code className="font-mono text-xs break-all text-ink">
                      {search.installation_id}
                    </code>
                  </Row>
                ) : null}

                <Row label="Permissions" icon={<ShieldCheck className="size-3.5" aria-hidden />}>
                  {scopes.length > 0 ? (
                    <ul className="flex flex-wrap gap-1.5">
                      {scopes.map((scope) => (
                        <li key={scope}>
                          <span className="inline-flex rounded-pill bg-surface-sunken px-2.5 py-1 font-mono text-2xs text-ink-3">
                            {scope}
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    "The permissions this application is registered for."
                  )}
                </Row>
              </dl>
            </CardBody>

            {callback && !callback.loopback ? (
              <CardBody className="pt-0">
                <Notice
                  tone="warning"
                  title="This callback is not on your machine"
                  icon={<ShieldAlert className="size-4" aria-hidden />}
                >
                  A desktop application returns to an address on this computer. Only continue
                  if you recognise{" "}
                  <code className="font-mono text-2xs break-all text-ink">
                    {callback.origin}
                  </code>
                  .
                </Notice>
              </CardBody>
            ) : null}

            <CardFooter className="flex flex-wrap items-center justify-end gap-2">
              <Button variant="secondary" onClick={() => setCancelled(true)}>
                <X className="size-4" aria-hidden />
                Cancel
              </Button>
              <Button
                disabled={!canSpend(session)}
                onClick={() => {
                  // A full navigation, not a fetch: the platform answers with
                  // a 302 to the application's callback, and only the browser
                  // following it hands control back to the waiting desktop
                  // listener.
                  window.location.assign(handoffURL(search));
                }}
              >
                Allow and return to the application
                <ArrowRight className="size-4" aria-hidden />
              </Button>
            </CardFooter>
          </Card>
        </div>
      )}
    </RequireSession>
  );
}

/** One labelled fact about the request. */
function Row({
  label,
  icon,
  children,
}: {
  label: string;
  icon: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="grid gap-1.5 py-4 sm:grid-cols-[11rem_minmax(0,1fr)] sm:items-baseline sm:gap-4">
      <dt className="flex items-center gap-2 text-xs font-semibold text-ink-4">
        <span className="text-ink-4">{icon}</span>
        {label}
      </dt>
      <dd className="min-w-0 text-sm text-ink-2">{children}</dd>
    </div>
  );
}

/**
 * The two terminal states — no request to show, and a request the customer
 * turned down. Both are dead ends by design: neither offers a way to continue,
 * because continuing is exactly what the customer did not ask for.
 */
function Outcome({
  title,
  description,
  tone,
  noticeTitle,
  icon,
  children,
}: {
  title: string;
  description: string;
  tone: "info" | "warning";
  noticeTitle: string;
  icon: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader title={title} description={description} />
      <Notice tone={tone} title={noticeTitle} icon={icon}>
        {children}
      </Notice>
      <div className="mt-6">
        <Button href="/" variant="secondary">
          Go to your account
          <ArrowRight className="size-4" aria-hidden />
        </Button>
      </div>
    </div>
  );
}
